import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "page-content" }
const _hoisted_2 = { class: "flex flex-wrap gap-2 mt-3" }
const _hoisted_3 = { key: 0 }
const _hoisted_4 = { key: 1 }
const _hoisted_5 = { class: "w-full flex flex-wrap mt-2" }
const _hoisted_6 = { class: "w-full flex flex-wrap gap-3" }

import { computed, onMounted, onBeforeUnmount, ref } from 'vue';
import { storeToRefs } from 'pinia';
import MultiSelect, { MultiSelectChangeEvent } from 'primevue/multiselect';
import { useRouting, useRequestCancel, can } from '@/composables';
import { useReportStore, useReviewReportStore } from '@/store';
import ReportSubjectTitle from '@/views/Reports/components/ReportSubjectTitle.vue';
import ReviewReportTrack from './ReviewReportTrack.vue';
import ReviewReportClinicianMinutes from './ReviewReportClinicianMinutes.vue';
import ReviewReportStudents from './ReviewReportStudents.vue';
import ReviewReportExitedStudents from './ReviewReportExitedStudents.vue';
import ReviewReportAttendance from './ReviewReportAttendance.vue';
import { PERMISSIONS, EMIT } from '@/enum';


export default /*@__PURE__*/_defineComponent({
  __name: 'ReviewReport',
  emits: [EMIT.QUERY_PARAMS],
  setup(__props, { emit: __emit }) {

const $routing = useRouting();

const emit = __emit;

const reportStore = useReportStore();

const reviewReportStore = useReviewReportStore();

const {
  filters: {
    value: { disciplines, eventTypes },
  },
} = storeToRefs(reviewReportStore);

const reportSubjectName = ref();

onMounted(() => init());

onBeforeUnmount(() => {
  useRequestCancel().requestSaverAbortAll();
  $routing.router.push({ query: {} });
});

$routing.routeChangeWatcher(async () => {
  if (isReviewReport.value) {
    reviewReportStore.queryParams = $routing.route.query;
    reviewReportStore.getEventTypes();
    reviewReportStore.getDisciplines();
    defineFilters();
    doReviewReport();
    defineReportName();
  }
});

const isReviewReport = computed<boolean>(() => $routing.route.name === 'reports.review');

const init = async () => {
  if (isReviewReport.value) {
    if (can([PERMISSIONS.ORGANIZATION_SERVICES_INDEX])) await reviewReportStore.getEventTypes();
    if (can([PERMISSIONS.ORGANIZATION_DISCIPLINES_INDEX])) await reviewReportStore.getDisciplines();
    await defineFilters();
    if (!reportStore.referrer) {
      await doReviewReport();
      await defineReportName();
      reportStore.referrer = $routing.route.name;
    }
  }
};

const doReviewReport = () => {
  const query = $routing.route.query;
  const filters = reviewReportStore.defineFilters(query);
  reviewReportStore.resetReviewReportData();
  reviewReportStore
    .getReviewReportTrack(filters)
    .then(() => doReviewReportClinicianMinutesAndStudents(filters))
    .catch(() => doReviewReportTherapyResultAndExitedStudents(filters));
};

const doReviewReportClinicianMinutesAndStudents = (filters) => {
  reviewReportStore.getReviewReportClinicianMinutes(filters);
  reviewReportStore
    .getReviewReportStudents(filters)
    .then(() => doReviewReportTherapyResultAndExitedStudents(filters))
    .catch(() => doReviewReportTherapyResultAndExitedStudents(filters));
};

const doReviewReportTherapyResultAndExitedStudents = (filters) => {
  reviewReportStore.getReviewReportTherapyResult(filters);
  reviewReportStore.getReviewReportExitedStudents(filters);
};

const defineFilters = async () => {
  const query = $routing.route.query;

  const disciplines: number[] = [];
  if (query.disciplines) {
    query.disciplines
      .toString()
      .split(',')
      .forEach((item) => disciplines.push(+item));
  }
  reviewReportStore.filters.disciplines.selected = query.disciplines ? disciplines : null;

  const eventTypes: number[] = [];
  if (query.event_types) {
    query.event_types
      .toString()
      .split(',')
      .forEach((item) => eventTypes.push(item));
  }
  reviewReportStore.filters.eventTypes.selected = query.event_types ? eventTypes : null;

  // Add back the query params for the report once it regains focus again.
  if (reviewReportStore.queryParams) {
    $routing.addQueryParams(reviewReportStore.queryParams);
  }

  emit(EMIT.QUERY_PARAMS);
};

const defineReportName = async () => (reportSubjectName.value = reportStore.clinicians || reportStore.organizations);

const onDisciplineChange = (event: MultiSelectChangeEvent) => {
  if (event.value.length) {
    $routing.addQueryParam('disciplines', event.value.join());
  } else {
    $routing.removeQueryParam('disciplines');
  }
};

const onEventTypeChange = (event: MultiSelectChangeEvent) => {
  if (event.value.length) {
    $routing.addQueryParam('event_types', event.value.join());
  } else {
    $routing.removeQueryParam('event_types');
  }
};

return (_ctx: any,_cache: any) => {
  const _component_Card = _resolveComponent("Card")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_component_Card, null, {
      content: _withCtx(() => [
        _createVNode(ReportSubjectTitle, {
          class: "pt-3",
          title: "Review",
          name: reportSubjectName.value,
          dates: _unref(reportStore).filters.dates
        }, null, 8, ["name", "dates"]),
        _createElementVNode("div", _hoisted_2, [
          (_unref(can)([_unref(PERMISSIONS).ORGANIZATION_DISCIPLINES_INDEX]))
            ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                _createVNode(_unref(MultiSelect), {
                  modelValue: _unref(disciplines).selected,
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((_unref(disciplines).selected) = $event)),
                  options: _unref(disciplines).options,
                  loading: _unref(disciplines).loading,
                  filter: true,
                  "reset-filter-on-hide": true,
                  class: "w-full",
                  "option-label": "name",
                  "option-value": "value",
                  "show-toggle-all": false,
                  placeholder: "All Disciplines",
                  display: "chip",
                  onChange: onDisciplineChange
                }, null, 8, ["modelValue", "options", "loading"])
              ]))
            : _createCommentVNode("", true),
          (_unref(can)([_unref(PERMISSIONS).ORGANIZATION_SERVICES_INDEX]))
            ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                _createVNode(_unref(MultiSelect), {
                  modelValue: _unref(eventTypes).selected,
                  "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((_unref(eventTypes).selected) = $event)),
                  options: _unref(eventTypes).options,
                  loading: _unref(eventTypes).loading,
                  filter: true,
                  "reset-filter-on-hide": true,
                  class: "w-full",
                  "option-label": "name",
                  "option-value": "value",
                  "show-toggle-all": false,
                  placeholder: "All Event Types",
                  display: "chip",
                  onChange: onEventTypeChange
                }, null, 8, ["modelValue", "options", "loading"])
              ]))
            : _createCommentVNode("", true)
        ]),
        _createVNode(ReviewReportTrack),
        _createElementVNode("div", _hoisted_5, [
          _createElementVNode("div", _hoisted_6, [
            _createVNode(ReviewReportClinicianMinutes, { class: "min-w-half flex-1" }),
            _createVNode(ReviewReportStudents, { class: "min-w-half flex-1" }),
            _createVNode(ReviewReportAttendance, { class: "min-w-half flex-1" }),
            _createVNode(ReviewReportExitedStudents, { class: "min-w-half flex-1" })
          ])
        ])
      ]),
      _: 1
    })
  ]))
}
}

})